/**
 * Ingest Queue Page
 *
 * Shows unfiled documents from email ingestion with AI-suggested metadata.
 * Allows users to file or reject documents.
 */

import { useState, useEffect } from 'react';
import { useEreg } from '../contexts';
import {
  getIngestQueue,
  fileIngestItem,
  rejectIngestItem,
  getIngestSuggestions,
  getProtocolFolders,
  getDocumentTypes,
} from '../lib/api';
import { PageHeader, fieldCls } from '../../ui';
import type { IngestQueueItem, Folder, DocumentType, FileDocumentRequest } from '../types';

export function IngestQueue() {
  const { currentProtocol, loading: contextLoading } = useEreg();
  const [items, setItems] = useState<IngestQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'unfiled' | 'all'>('unfiled');
  const [selectedItem, setSelectedItem] = useState<IngestQueueItem | null>(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  useEffect(() => {
    if (currentProtocol?.id) {
      loadQueue();
    }
  }, [currentProtocol?.id, filter]);

  async function loadQueue() {
    if (!currentProtocol?.id) return;

    try {
      setLoading(true);
      const data = await getIngestQueue(currentProtocol.id, filter);
      setItems(data);
    } catch (error) {
      console.error('Failed to load ingest queue:', error);
    } finally {
      setLoading(false);
    }
  }

  function openFileModal(item: IngestQueueItem) {
    setSelectedItem(item);
    setIsFileModalOpen(true);
  }

  function openRejectModal(item: IngestQueueItem) {
    setSelectedItem(item);
    setIsRejectModalOpen(true);
  }

  async function handleFileFiled() {
    setIsFileModalOpen(false);
    setSelectedItem(null);
    await loadQueue();
  }

  async function handleItemRejected() {
    setIsRejectModalOpen(false);
    setSelectedItem(null);
    await loadQueue();
  }

  function formatFileSize(bytes?: number): string {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString();
  }

  function getStatusBadge(status: string) {
    const styles: Record<string, string> = {
      unfiled: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
      filed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  }

  if (contextLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" />
      </div>
    );
  }

  if (!currentProtocol) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No Protocol Selected</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Please select a protocol to view the ingest queue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Email Repository"
        subtitle="Review and file documents received via email"
        actions={
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'unfiled' | 'all')}
            className={fieldCls}
          >
            <option value="unfiled">Unfiled Only</option>
            <option value="all">All Items</option>
          </select>
        }
      />

      {/* Email Ingest Address */}
      {currentProtocol.ingest_email_address && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 dark:bg-primary-900/20 dark:border-primary-800">
          <div className="flex items-start gap-3">
            <div className="text-primary-600 dark:text-primary-400 mt-0.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-primary-900 dark:text-primary-200">Forward documents to this address:</p>
              <p className="text-sm text-primary-700 dark:text-primary-300 font-mono mt-1">{currentProtocol.ingest_email_address}</p>
            </div>
          </div>
        </div>
      )}

      {/* Queue Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No Documents in Queue</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Documents forwarded via email will appear here for filing.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Document
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Received
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
              {items.map((item) => (
                <tr key={item.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center dark:bg-gray-700">
                        <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {item.original_filename}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatFileSize(item.file_size_bytes)} • {item.mime_type?.split('/')[1]?.toUpperCase() || 'Unknown'}
                        </div>
                        {item.warnings && item.warnings.length > 0 && (
                          <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                            {item.warnings[0]}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{item.source_email_from || '-'}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                      {item.source_email_subject || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(item.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(item.status)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {item.status === 'unfiled' && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openFileModal(item)}
                          className="px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
                        >
                          File
                        </button>
                        <button
                          onClick={() => openRejectModal(item)}
                          className="px-3 py-1.5 text-gray-600 text-sm font-medium transition-colors duration-150 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {item.status === 'filed' && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Filed</span>
                    )}
                    {item.status === 'rejected' && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Rejected</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* File Modal */}
      {isFileModalOpen && selectedItem && currentProtocol && (
        <FileModal
          item={selectedItem}
          protocolId={currentProtocol.id}
          siteId={currentProtocol.site_id}
          onClose={() => setIsFileModalOpen(false)}
          onFiled={handleFileFiled}
        />
      )}

      {/* Reject Modal */}
      {isRejectModalOpen && selectedItem && (
        <RejectModal
          item={selectedItem}
          onClose={() => setIsRejectModalOpen(false)}
          onRejected={handleItemRejected}
        />
      )}
    </div>
  );
}

// File Modal Component
function FileModal({
  item,
  protocolId,
  siteId,
  onClose,
  onFiled,
}: {
  item: IngestQueueItem;
  protocolId: string;
  siteId: string;
  onClose: () => void;
  onFiled: () => void;
}) {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const [formData, setFormData] = useState<FileDocumentRequest>({
    folder_id: item.suggested_folder_id || '',
    document_name: item.suggested_document_name || item.original_filename.replace(/\.[^/.]+$/, ''),
    document_type: item.suggested_document_type || '',
    document_date: new Date().toISOString().split('T')[0],
    version_number: '1.0',
    include_body_as_pdf: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [foldersData, typesData] = await Promise.all([
        getProtocolFolders(protocolId),
        getDocumentTypes({ site_id: siteId }),
      ]);
      setFolders(flattenFolders(foldersData));
      setDocumentTypes(typesData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  function flattenFolders(folders: Folder[], prefix = ''): Folder[] {
    return folders.reduce((acc: Folder[], folder) => {
      const displayFolder = { ...folder, name: prefix + folder.name };
      acc.push(displayFolder);
      if (folder.children) {
        acc.push(...flattenFolders(folder.children, prefix + '  '));
      }
      return acc;
    }, []);
  }

  async function loadSuggestions() {
    try {
      setLoadingSuggestions(true);
      const suggestions = await getIngestSuggestions(item.id);
      setFormData(prev => ({
        ...prev,
        document_name: suggestions.document_name || prev.document_name,
        document_type: suggestions.document_type || prev.document_type,
        folder_id: suggestions.folder_id || prev.folder_id,
      }));
    } catch (error) {
      console.error('Failed to get suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!formData.folder_id || !formData.document_name || !formData.document_type) {
      return;
    }

    try {
      setSubmitting(true);
      await fileIngestItem(item.id, formData);
      onFiled();
    } catch (error) {
      console.error('Failed to file document:', error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto dark:bg-gray-800">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">File Document</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{item.original_filename}</p>
        </div>

        {loading ? (
          <div className="p-6 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* AI Suggestions Button */}
            <button
              type="button"
              onClick={loadSuggestions}
              disabled={loadingSuggestions}
              className="w-full px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 flex items-center justify-center gap-2 dark:border-primary-700 dark:text-primary-400 dark:hover:bg-primary-900/20"
            >
              {loadingSuggestions ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 dark:border-primary-400" />
                  Getting AI Suggestions...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Get AI Suggestions
                </>
              )}
            </button>

            {/* Document Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Name *
              </label>
              <input
                type="text"
                value={formData.document_name}
                onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                className={`w-full ${fieldCls}`}
                required
              />
            </div>

            {/* Folder */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Folder *
              </label>
              <select
                value={formData.folder_id}
                onChange={(e) => setFormData({ ...formData, folder_id: e.target.value })}
                className={`w-full ${fieldCls}`}
                required
              >
                <option value="">Select a folder...</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Document Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Type *
              </label>
              <select
                value={formData.document_type}
                onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                className={`w-full ${fieldCls}`}
                required
              >
                <option value="">Select a type...</option>
                {documentTypes.map((type) => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Document Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Date
              </label>
              <input
                type="date"
                value={formData.document_date || ''}
                onChange={(e) => setFormData({ ...formData, document_date: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>

            {/* Version */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Version
              </label>
              <input
                type="text"
                value={formData.version_number || '1.0'}
                onChange={(e) => setFormData({ ...formData, version_number: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>

            {/* Include Email Body */}
            {item.source_email_body && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.include_body_as_pdf}
                  onChange={(e) => setFormData({ ...formData, include_body_as_pdf: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Include email body as cover page</span>
              </label>
            )}

            {/* Email Context */}
            {item.source_email_subject && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm dark:bg-gray-900/40">
                <p className="font-medium text-gray-700 dark:text-gray-300">Email Context:</p>
                <p className="text-gray-600 dark:text-gray-400 mt-1">From: {item.source_email_from}</p>
                <p className="text-gray-600 dark:text-gray-400">Subject: {item.source_email_subject}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 transition-colors duration-150 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !formData.folder_id || !formData.document_name || !formData.document_type}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Filing...' : 'File Document'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Reject Modal Component
function RejectModal({
  item,
  onClose,
  onRejected,
}: {
  item: IngestQueueItem;
  onClose: () => void;
  onRejected: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleReject() {
    try {
      setSubmitting(true);
      await rejectIngestItem(item.id, reason);
      onRejected();
    } catch (error) {
      console.error('Failed to reject item:', error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full dark:bg-gray-800">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reject Document</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{item.original_filename}</p>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This will remove the document from the queue. This action cannot be undone.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this document being rejected?"
              rows={3}
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 transition-colors duration-150 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={submitting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50"
            >
              {submitting ? 'Rejecting...' : 'Reject Document'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IngestQueue;
